import { Copy, Download, Upload } from 'lucide-react';
import * as React from 'react';
import { showToast } from '../../toast';
import { t } from '../config/strings';
import { deleteDraft, readDraft, writeDraft } from '../io/drafts';
import {
  buildSecPoseDraftExport,
  countDraftEditedFrames,
  encryptSecPoseDraft,
  generateRecoveryCode,
  inspectSecPoseDraftFile,
  MAX_ENCRYPTED_DRAFT_BYTES,
  type ParsedSecPoseDraftFile,
  parseSecPoseDraftFile,
  type SecPoseDraftImportCandidate,
  type SecPoseDraftSource,
  SecPoseDraftTransferError,
  validateSecPoseDraftImport,
} from '../io/encryptedDraft';
import { buildDraft } from '../lib/draft';
import { useStore } from '../store';
import type { Draft } from '../types';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';

export interface SecPoseDraftTransferControlsProps {
  episodeId: string;
  operator: string;
  leaseId: string | null;
  editable: boolean;
  source: SecPoseDraftSource;
}

interface SelectedDraftFile {
  name: string;
  text: string;
  kind: ParsedSecPoseDraftFile['kind'];
}

interface ValidatedImport {
  candidate: SecPoseDraftImportCandidate;
  currentDraft: Draft | null;
}

function formatDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return hours > 0
    ? `${hours}h ${minutes}m ${remainder}s`
    : minutes > 0
      ? `${minutes}m ${remainder}s`
      : `${remainder}s`;
}

function shortFingerprint(value: string): string {
  return `${value.slice(0, 15)}…${value.slice(-6)}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof SecPoseDraftTransferError) return error.message;
  return error instanceof Error ? error.message : 'SecPose draft operation failed.';
}

function triggerJsonDownload(payload: unknown): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `secpose-draft-encrypted-${Date.now()}.json`;
  link.click();
  queueMicrotask(() => URL.revokeObjectURL(url));
}

function DraftSummary({ label, draft }: { label: string; draft: Draft }): React.ReactElement {
  return (
    <section className="border border-border p-3 text-sm">
      <h3 className="mb-2 font-semibold">{label}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">{t.draftTransfer.savedAt}</dt>
        <dd>{new Date(draft.savedAt).toLocaleString()}</dd>
        <dt className="text-muted-foreground">{t.draftTransfer.elapsed}</dt>
        <dd>{formatDuration(draft.elapsedMs ?? 0)}</dd>
        <dt className="text-muted-foreground">{t.draftTransfer.editedFrames}</dt>
        <dd>{countDraftEditedFrames(draft)}</dd>
      </dl>
    </section>
  );
}

export function SecPoseDraftTransferControls({
  episodeId,
  operator,
  leaseId,
  editable,
  source,
}: SecPoseDraftTransferControlsProps): React.ReactElement | null {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [recoveryCode, setRecoveryCode] = React.useState('');
  const [codeSaved, setCodeSaved] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [exported, setExported] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<SelectedDraftFile | null>(null);
  const [enteredCode, setEnteredCode] = React.useState('');
  const [validatedImport, setValidatedImport] = React.useState<ValidatedImport | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!editable) return null;

  const resetImport = (): void => {
    setSelectedFile(null);
    setEnteredCode('');
    setValidatedImport(null);
    setBusy(false);
    setError(null);
  };

  const openExport = (): void => {
    setRecoveryCode(generateRecoveryCode());
    setCodeSaved(false);
    setExported(false);
    setError(null);
    setExportOpen(true);
  };

  const closeExport = (): void => {
    setExportOpen(false);
    setRecoveryCode('');
    setCodeSaved(false);
    setExported(false);
    setError(null);
  };

  const copyRecoveryCode = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      showToast(t.draftTransfer.codeCopied);
    } catch (copyError) {
      setError(errorMessage(copyError));
    }
  };

  const downloadEncryptedDraft = async (): Promise<void> => {
    setExporting(true);
    setError(null);
    try {
      const draft = buildDraft(useStore.getState());
      const payload = buildSecPoseDraftExport({ episodeId, operator, source, draft });
      const encrypted = await encryptSecPoseDraft(payload, recoveryCode);
      triggerJsonDownload(encrypted);
      setExported(true);
    } catch (exportError) {
      setError(errorMessage(exportError));
    } finally {
      setExporting(false);
    }
  };

  const chooseFile = (): void => {
    if (!leaseId) {
      setError(t.draftTransfer.leaseRequired);
      setImportOpen(true);
      return;
    }
    fileInputRef.current?.click();
  };

  const selectFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    resetImport();
    setImportOpen(true);
    try {
      if (file.size <= 0 || file.size > MAX_ENCRYPTED_DRAFT_BYTES) {
        throw new SecPoseDraftTransferError(
          'file_too_large',
          'Draft file must be between 1 byte and 20 MiB.'
        );
      }
      const text = await file.text();
      setSelectedFile({ name: file.name, text, kind: inspectSecPoseDraftFile(text) });
    } catch (fileError) {
      setError(errorMessage(fileError));
    }
  };

  const validateSelectedFile = async (): Promise<void> => {
    if (!selectedFile) return;
    setBusy(true);
    setError(null);
    try {
      const parsed = await parseSecPoseDraftFile(
        selectedFile.text,
        selectedFile.kind === 'encrypted' ? enteredCode : undefined
      );
      const candidate = validateSecPoseDraftImport(parsed, {
        episodeId,
        operator,
        leaseId,
        editable,
        source,
      });
      const currentDraft = await readDraft(episodeId);
      setValidatedImport({ candidate, currentDraft });
    } catch (validationError) {
      setError(errorMessage(validationError));
    } finally {
      setBusy(false);
    }
  };

  const restoreDraft = async (): Promise<void> => {
    if (!validatedImport) return;
    setBusy(true);
    setError(null);
    try {
      try {
        await writeDraft(episodeId, validatedImport.candidate.draft);
      } catch {
        throw new SecPoseDraftTransferError(
          'persist_failed',
          'Unable to save the imported draft in this browser.'
        );
      }
      const store = useStore.getState();
      try {
        store.restoreFromDraft(validatedImport.candidate.draft);
        store.startEpisodeTimer();
      } catch {
        try {
          if (validatedImport.currentDraft) {
            await writeDraft(episodeId, validatedImport.currentDraft);
          } else {
            await deleteDraft(episodeId);
          }
        } catch {
          throw new SecPoseDraftTransferError(
            'persist_failed',
            'Draft restore failed and the previous local draft could not be restored.'
          );
        }
        throw new SecPoseDraftTransferError(
          'persist_failed',
          'Draft restore failed. The previous local draft was preserved.'
        );
      }
      showToast(t.draftTransfer.restored);
      setImportOpen(false);
      resetImport();
    } catch (restoreError) {
      setError(errorMessage(restoreError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 px-0"
          onClick={openExport}
          title={t.draftTransfer.exportTitle}
          aria-label={t.draftTransfer.exportTitle}
          data-testid="secpose-export-draft"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 px-0"
          onClick={chooseFile}
          disabled={!leaseId}
          title={leaseId ? t.draftTransfer.importTitle : t.draftTransfer.leaseRequired}
          aria-label={t.draftTransfer.importTitle}
          data-testid="secpose-import-draft"
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="application/json,.json"
          onChange={(event) => void selectFile(event)}
          data-testid="secpose-draft-file-input"
        />
      </div>

      <Dialog
        open={exportOpen}
        onOpenChange={(open) => (open ? setExportOpen(true) : closeExport())}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.draftTransfer.exportDialogTitle}</DialogTitle>
            <DialogDescription>{t.draftTransfer.exportDialogDesc}</DialogDescription>
          </DialogHeader>
          <div className="my-5 space-y-4">
            <label className="block text-sm font-medium" htmlFor="secpose-recovery-code">
              {t.draftTransfer.recoveryCode}
            </label>
            <div className="flex gap-2">
              <Input
                id="secpose-recovery-code"
                value={recoveryCode}
                readOnly
                className="font-mono"
              />
              <Button
                variant="outline"
                className="w-9 px-0"
                onClick={() => void copyRecoveryCode()}
                title={t.draftTransfer.copyCode}
                aria-label={t.draftTransfer.copyCode}
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={codeSaved}
                onChange={(event) => setCodeSaved(event.target.checked)}
              />
              <span>{t.draftTransfer.codeSaved}</span>
            </label>
            {exported && <p className="text-sm text-emerald-500">{t.draftTransfer.exported}</p>}
            {error && (
              <p className="text-sm text-red-500" role="alert">
                {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeExport}>
              {t.draftTransfer.cancel}
            </Button>
            <Button
              disabled={!codeSaved || exporting}
              onClick={() => void downloadEncryptedDraft()}
            >
              {exporting ? t.submit.saving : t.draftTransfer.download}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) resetImport();
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t.draftTransfer.importDialogTitle}</DialogTitle>
            <DialogDescription>{t.draftTransfer.importDialogDesc}</DialogDescription>
          </DialogHeader>
          <div className="my-5 space-y-4">
            {!selectedFile && !error && (
              <p className="text-sm text-muted-foreground">{t.draftTransfer.chooseFile}</p>
            )}
            {selectedFile && !validatedImport && (
              <>
                <p className="truncate text-sm font-medium">{selectedFile.name}</p>
                {selectedFile.kind === 'encrypted' ? (
                  <Input
                    type="password"
                    value={enteredCode}
                    onChange={(event) => setEnteredCode(event.target.value)}
                    placeholder={t.draftTransfer.enterCode}
                    autoComplete="off"
                  />
                ) : (
                  <p className="border-l-2 border-amber-500 pl-3 text-sm text-amber-500">
                    {t.draftTransfer.legacyWarning}
                  </p>
                )}
              </>
            )}
            {validatedImport && (
              <>
                {validatedImport.candidate.legacy && (
                  <p className="border-l-2 border-amber-500 pl-3 text-sm text-amber-500">
                    {t.draftTransfer.legacyWarning}
                  </p>
                )}
                <DraftSummary
                  label={t.draftTransfer.importedDraft}
                  draft={validatedImport.candidate.draft}
                />
                {validatedImport.currentDraft ? (
                  <DraftSummary
                    label={t.draftTransfer.currentDraft}
                    draft={validatedImport.currentDraft}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{t.draftTransfer.noCurrentDraft}</p>
                )}
                {validatedImport.candidate.source && (
                  <div className="text-xs text-muted-foreground">
                    <p>{t.draftTransfer.sourceVerified}</p>
                    <p className="font-mono">
                      {shortFingerprint(validatedImport.candidate.source.calibration_fingerprint)} ·{' '}
                      {shortFingerprint(validatedImport.candidate.source.asset_fingerprint)}
                    </p>
                  </div>
                )}
                <p className="text-sm font-medium text-amber-500">
                  {t.draftTransfer.overwriteWarning}
                </p>
              </>
            )}
            {error && (
              <p className="text-sm text-red-500" role="alert">
                {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setImportOpen(false);
                resetImport();
              }}
            >
              {t.draftTransfer.cancel}
            </Button>
            {selectedFile && !validatedImport && (
              <Button
                disabled={
                  busy || (selectedFile.kind === 'encrypted' && enteredCode.trim().length === 0)
                }
                onClick={() => void validateSelectedFile()}
              >
                {busy ? t.draftTransfer.validating : t.draftTransfer.validate}
              </Button>
            )}
            {validatedImport && (
              <Button disabled={busy} onClick={() => void restoreDraft()}>
                {busy ? t.draftTransfer.restoring : t.draftTransfer.restore}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
